/**
 * Admin Access Control helper.
 *
 * Adds a second verification layer on top of `useAuth().isAuthed`:
 *   - The session email MUST be present in `ADMIN_ACCOUNTS`.
 *   - The session storage entry (`rawan-auth-v2`) MUST be readable and
 *     contain the same email currently exposed in the auth context.
 *
 * Use `useIsAdmin()` everywhere the UI exposes destructive / privileged
 * controls (legal page editor, history rollback, etc.) — never rely on
 * `isAuthed` alone for those.
 */
import { useEffect, useState } from "react";
import { useAuth, ADMIN_ACCOUNTS } from "@/lib/auth";

const STORAGE_KEY = "rawan-auth-v2";

export function useIsAdmin(): boolean {
  const { isAuthed, email, ready } = useAuth();
  const [storageOk, setStorageOk] = useState(false);

  useEffect(() => {
    try {
      const raw = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
      if (!raw) { setStorageOk(false); return; }
      const parsed = JSON.parse(raw) as { email?: string };
      setStorageOk(!!parsed?.email && parsed.email === email);
    } catch {
      setStorageOk(false);
    }
  }, [email, isAuthed]);

  if (!ready || !isAuthed || !email) return false;
  if (!storageOk) return false;
  const normalized = email.trim().toLowerCase();
  return ADMIN_ACCOUNTS.some((a) => a.email === normalized);
}
